import React from 'react';
import { CircularProgress, Alert } from '@mui/material';

/**
 * Reusable PayoutTable component for displaying payout data
 * 
 * @param {Object} props
 * @param {Array} props.payouts - Array of payout data to display
 * @param {boolean} props.isLoading - Loading state
 * @param {Object} props.error - Error object if any
 * @param {string} props.emptyMessage - Message to display when no payouts are available
 * @param {boolean} props.isPending - Whether this table shows pending payouts (with actions) or history
 * @param {Function} props.onApprove - Function to call when approving a payout (admin only)
 * @param {Function} props.onDecline - Function to call when declining a payout (admin only)
 * @param {boolean} props.isAdmin - Whether this table is being used in admin view
 */
const PayoutTable = ({ 
    payouts = [], 
    isLoading, 
    error, 
    emptyMessage = "No payouts available", 
    isPending = false,
    onApprove,
    onDecline,
    isAdmin = false
}) => {
    // Helper function to determine status class
    const getStatusClass = (status) => {
        const statusMap = {
            "Approved": "s-green",
            "APPROVED": "s-green",
            "Pending": "s-orange",
            "PENDING": "s-orange",
            "Declined": "s-red n-right",
            "DECLINED": "s-red n-right"
        };
        return statusMap[status] || "";
    };

    // Helper function to format status text
    const formatStatus = (status) => {
        const statusMap = {
            "APPROVED": "Approved",
            "PENDING": "Pending",
            "DECLINED": "Declined"
        };
        return statusMap[status] || status;
    };

    // Helper function to get user email from different data structures
    const getUserEmail = (payout) => {
        console.log(payout, "payout")
        if (payout.email) return payout.email;
        if (payout.user_profile?.email) return payout.user_profile.email;
        if(payout.userProfile?.email) return payout.userProfile.email;
        return "N/A";
    };

    // Helper function to format date values
    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        
        // If it's already a formatted date string with slashes, return as is
        if (typeof dateStr === 'string' && dateStr.includes('/')) return dateStr;
        
        try {
            // Try to parse and format the date
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr; // If invalid date, return original
            
            return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
        } catch (e) {
            return dateStr;
        }
    };

    // Helper function to get property with fallbacks
    const getProperty = (payout, properties, defaultValue = "N/A") => {
        for (const prop of properties) {
            if (payout[prop] !== undefined) return payout[prop];
        }
        return defaultValue;
    };

    return (
        <div className="table-responsive mt-3">
            {/* Loading States */}
            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
                    <CircularProgress />
                </div>
            )}

            {/* Error States */}
            {error && (
                <Alert severity="error" sx={{ margin: '20px 0' }}>
                    {error.message || 'Failed to load payouts'}
                </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !error && payouts.length === 0 && (
                <div className="text-center py-5">
                    <h5>{emptyMessage}</h5>
                </div>
            )}

            {!isLoading && !error && payouts.length > 0 && (
                <table className="table dash-table">
                    <thead>
                        <tr>
                            {isAdmin && <td>User</td>}
                            <td>Requested at</td>
                            <td>Payment Method</td>
                            <td>Currency</td>
                            <td>Amount</td>
                            <td>Payment Date</td>
                            {!isPending && <td>Status</td>}
                            {isPending && <td>Action</td>}
                        </tr>
                    </thead>
                    <tbody>
                        {payouts.map((payout, i) => (
                            <tr key={i}>
                                {isAdmin && (
                                    <td>
                                        <div className="table-item">
                                            {getUserEmail(payout)}
                                        </div>
                                    </td>
                                )}
                                <td>
                                    <div className="table-item">
                                        {formatDate(getProperty(payout, ['requestDate', 'request_date']))}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-item">
                                        {getProperty(payout, ['method', 'payment_method'])}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-item">
                                        {getProperty(payout, ['currency'], "USD")}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-item">
                                        {getProperty(payout, ['amount'], 0)}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-item">
                                        {formatDate(getProperty(payout, ['paymentDate', 'payment_date']))}
                                    </div>
                                </td>
                                {!isPending && (
                                    <td>
                                        <div className={`release-status ${getStatusClass(payout.status)}`}>
                                            {formatStatus(payout.status)}
                                        </div>
                                    </td>
                                )}
                                {isPending && isAdmin && (
                                    <td>
                                        <div className="table-item d-flex">
                                            <button 
                                                className="table-action-btn a-red me-1"
                                                onClick={() => onDecline(payout.key, payout.id)}
                                            >
                                                <i className="fi fi-br-cross"></i>
                                            </button>
                                            <button 
                                                className="table-action-btn a-green"
                                                onClick={() => onApprove(payout.key, payout.id, payout.amount)}
                                            >
                                                <i className="fi fi-br-check"></i>
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default PayoutTable; 